import React, { useRef, useEffect, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import './WhyGenix.css';

const features = [
  'AI Health Assistance',
  'Consultation Summaries',
  'Telehealth Integration',
  'Integrated Communication Threats (Calls, SMS)',
  'Medication Reminders',
  'Voice-Based Appointment Booking'
];

const WhyGenix = () => {
  const videoRef = useRef(null);
  const videoContainerRef = useRef(null);
  const sectionRef = useRef(null);
  const [isMuted, setIsMuted] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  const isInView = useInView(sectionRef, { 
    once: true,
    margin: "-20% 0px -20% 0px",
    amount: 0.4
  });

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play().catch(error => {
          console.log("Play failed:", error);
        });
      }
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const skipForward = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = Math.min(videoRef.current.currentTime + 10, videoRef.current.duration);
    }
  };

  const skipBackward = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = Math.max(videoRef.current.currentTime - 10, 0);
    }
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      videoContainerRef.current.requestFullscreen().catch(err => {
        console.log(`Error attempting to enable fullscreen: ${err.message}`);
      });
    } else {
      document.exitFullscreen();
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  };

  const handleProgressClick = (e) => {
    const progressBar = e.currentTarget;
    const rect = progressBar.getBoundingClientRect();
    const pos = (e.clientX - rect.left) / rect.width;
    if (videoRef.current) {
      videoRef.current.currentTime = pos * videoRef.current.duration;
    }
  };

  const formatTime = (timeInSeconds) => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    const video = videoRef.current;
    const container = videoContainerRef.current;
    if (!video || !container) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          video.muted = isMuted;
          video.play().then(() => {
            setIsPlaying(true);
          }).catch(error => {
            console.log("Auto-play failed:", error);
          });
        } else {
          video.pause();
          setIsPlaying(false);
        }
      },
      { threshold: 0.3 }
    );

    observer.observe(sectionRef.current);

    // Add event listeners
    video.addEventListener('play', () => setIsPlaying(true));
    video.addEventListener('pause', () => setIsPlaying(false));
    video.addEventListener('ended', () => setIsPlaying(false));
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    
    document.addEventListener('fullscreenchange', () => {
      setIsFullscreen(!!document.fullscreenElement);
    });

    // Handle keyboard shortcuts
    const handleKeyPress = (e) => {
      if (container.contains(e.target)) {
        switch(e.key.toLowerCase()) {
          case 'k':
          case ' ':
            e.preventDefault();
            togglePlay();
            break;
          case 'm':
            toggleMute();
            break;
          case 'f':
            toggleFullscreen();
            break;
          case 'arrowleft':
            skipBackward();
            break;
          case 'arrowright':
            skipForward();
            break;
          default:
            break;
        }
      }
    };

    document.addEventListener('keydown', handleKeyPress);

    return () => {
      observer.disconnect();
      video.removeEventListener('play', () => setIsPlaying(true));
      video.removeEventListener('pause', () => setIsPlaying(false));
      video.removeEventListener('ended', () => setIsPlaying(false));
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      document.removeEventListener('fullscreenchange', () => {
        setIsFullscreen(!!document.fullscreenElement);
      });
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [isMuted]);

  return (
    <section className="why-genix-section" ref={sectionRef}>
      <div className="why-genix-container new-why-genix-layout">
        <div className="why-genix-video-col">
          <div className="video-container" ref={videoContainerRef}>
            <video
              ref={videoRef}
              className="custom-video"
              loop
              playsInline
              poster="/images/video-poster.jpg"
              muted={isMuted}
              onClick={togglePlay}
            >
              <source src="/images/GenixAi.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
            <div className="youtube-style-controls">
              <div className="progress-bar" onClick={handleProgressClick}>
                <div 
                  className="progress-filled" 
                  style={{ width: `${(currentTime / duration) * 100}%` }}
                />
              </div>
              
              <div className="controls-main">
                <div className="controls-left">
                  <button onClick={togglePlay} className="control-button play-button">
                    {isPlaying ? 
                      <svg viewBox="0 0 24 24"><path fill="currentColor" d="M14,19H18V5H14M6,19H10V5H6V19Z" /></svg> : 
                      <svg viewBox="0 0 24 24"><path fill="currentColor" d="M8,5.14V19.14L19,12.14L8,5.14Z" /></svg>
                    }
                  </button>
                  <button onClick={skipBackward} className="control-button">
                    <svg viewBox="0 0 24 24"><path fill="currentColor" d="M12.5,3C17.15,3 21.08,6.03 22.47,10.22L20.1,11C19.05,7.81 16.04,5.5 12.5,5.5C10.54,5.5 8.77,6.22 7.38,7.38L10,10H3V3L5.6,5.6C7.45,4 9.85,3 12.5,3M10,12V22H8V14H6V12H10M18,14V20C18,21.11 17.11,22 16,22H14A2,2 0 0,1 12,20V14A2,2 0 0,1 14,12H16C17.11,12 18,12.9 18,14M14,14V20H16V14H14M11.5,3C14.15,3 16.55,4 18.4,5.6L21,3V10H14L16.62,7.38C15.23,6.22 13.46,5.5 11.5,5.5C7.96,5.5 4.95,7.81 3.9,11L1.53,10.22C2.92,6.03 6.85,3 11.5,3Z" /></svg>
                  </button>
                  <button onClick={skipForward} className="control-button">
                    <svg viewBox="0 0 24 24"><path fill="currentColor" d="M10,12V22H8V14H6V12H10M18,14V20C18,21.11 17.11,22 16,22H14A2,2 0 0,1 12,20V14A2,2 0 0,1 14,12H16C17.11,12 18,12.9 18,14M14,14V20H16V14H14M11.5,3C14.15,3 16.55,4 18.4,5.6L21,3V10H14L16.62,7.38C15.23,6.22 13.46,5.5 11.5,5.5C7.96,5.5 4.95,7.81 3.9,11L1.53,10.22C2.92,6.03 6.85,3 11.5,3Z" /></svg>
                  </button>
                  <div className="time-display">
                    <span>{formatTime(currentTime)}</span>
                    <span> / </span>
                    <span>{formatTime(duration)}</span>
                  </div>
                </div>
                
                <div className="controls-right">
                  <button onClick={toggleMute} className="control-button">
                    {isMuted ? 
                      <svg viewBox="0 0 24 24"><path fill="currentColor" d="M12,4L9.91,6.09L12,8.18M4.27,3L3,4.27L7.73,9H3V15H7L12,20V13.27L16.25,17.53C15.58,18.04 14.83,18.46 14,18.7V20.77C15.38,20.45 16.63,19.82 17.68,18.96L19.73,21L21,19.73L12,10.73M19,12C19,12.94 18.8,13.82 18.46,14.64L19.97,16.15C20.62,14.91 21,13.5 21,12C21,7.72 18,4.14 14,3.23V5.29C16.89,6.15 19,8.83 19,12M16.5,12C16.5,10.23 15.5,8.71 14,7.97V10.18L16.45,12.63C16.5,12.43 16.5,12.21 16.5,12Z" /></svg> :
                      <svg viewBox="0 0 24 24"><path fill="currentColor" d="M14,3.23V5.29C16.89,6.15 19,8.83 19,12C19,15.17 16.89,17.84 14,18.7V20.77C18,19.86 21,16.28 21,12C21,7.72 18,4.14 14,3.23M16.5,12C16.5,10.23 15.5,8.71 14,7.97V16C15.5,15.29 16.5,13.76 16.5,12M3,9V15H7L12,20V4L7,9H3Z" /></svg>
                    }
                  </button>
                  <button onClick={toggleFullscreen} className="control-button">
                    {isFullscreen ?
                      <svg viewBox="0 0 24 24"><path fill="currentColor" d="M14,14H19V16H16V19H14V14M5,14H10V19H8V16H5V14M8,5H10V10H5V8H8V5M19,8V10H14V5H16V8H19Z" /></svg> :
                      <svg viewBox="0 0 24 24"><path fill="currentColor" d="M5,5H10V7H7V10H5V5M14,5H19V10H17V7H14V5M17,14H19V19H14V17H17V14M10,17V19H5V14H7V17H10Z" /></svg>
                    }
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="why-genix-text-col">
          <div className="why-genix-heading-overlap">
            <h1>The Capital of new beginnings</h1>
          </div>
          <div className="why-genix-paragraph-box">
            <ul className="why-genix-feature-list">
              <li>AI Health Assistance</li>
              <li>Consultation Summaries</li>
              <li>Telehealth Integration</li>
              <li>Integrated Communication Threats (Calls, SMS)</li>
              <li>Medication Reminders</li>
              <li>Voice-Based Appointment Booking</li>
            </ul>
            
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyGenix; 